import { computed, ref, toValue } from 'vue';
import { getAnalysisLogAPI } from '@/api/AnalysisApi';
import { DEV_MODE } from '@/config/dev-mode';

/**
 * 낭만작명 구매 내역 데이터/필터 composable.
 *
 * - PC `CoverPanelNaming.vue` 와 모바일 `m/saju-insight/nangman-naming.vue` 시트가
 *   동일한 데이터/카테고리 분류 로직을 공유한다.
 * - 활성 카테고리 상태는 각 컴포넌트가 자체적으로 관리한다 (시트 reset 정책이 달라서).
 *
 * 동작:
 * - 첫 진입 시 `getAnalysisLogAPI({ analysisTypeId:47 })`를 namingTarget 없이 1회 호출
 *   → 전체 작명 이력을 한 번에 받아서 client-side로 카테고리 분류.
 * - 카테고리 탭 disabled 상태(`categoryPurchaseMap`)를 단일 호출로 계산 가능.
 * - 응답 항목의 `guestUserName`/`extraJson`을 카드 표시 구조로 매핑.
 * - 모듈 레벨 캐시 — PC/Mobile이 동일 데이터 공유.
 */

/** 카테고리 탭 — 모드키 두 번째 토큰(type-TARGET-detail의 TARGET)과 매칭 */
export const PURCHASE_CATEGORY_TABS = [
  { key: 'person', label: '사람 이름' },
  { key: 'pet', label: '반려 · 사물' },
  { key: 'biz', label: '사업 · 상호' },
  { key: 'culture', label: '문화 · 아호' },
];

const NAMING_TYPE_ID = 47;

/** 전체 작명 로그 — null이면 미로드 상태 */
const allLogs = ref(null);
const isFetching = ref(false);
let fetchPromise = null;
/** reset 세대 — 리셋 이전에 시작된 in-flight 응답이 캐시를 되살리지 못하게 막는다 (로그아웃 중 응답 도착 등) */
let fetchEpoch = 0;

const formatDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
};

const parseExtraJson = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return {}; }
  }
  return raw;
};

const mapLogToHistoryItem = (log) => {
  const extra = parseExtraJson(log?.extraJson);
  return {
    id: String(log.analysisLogId ?? `${log.guestUserId}-${log.createdAt}`),
    analysisLogId: log.analysisLogId,
    guestUserId: log.guestUserId,
    sajuName: log.guestUserName || '',
    modeKey: extra.modeKey || '',
    detailInput: extra.detailInput || {},
    purchaseDate: formatDate(log.createdAt),
    validUntil: formatDate(log.expiryDate),
    raw: log,
  };
};

/** modeKey의 두 번째 토큰(target)으로 카테고리 식별. 예: 'recommend-person-kor' → 'person' */
const getCategoryOf = (item) => item.modeKey?.split('-')[1] ?? '';

/** 캐시 무효화 — 결제 성공/수동 새로고침/로그아웃(clearAllUserCaches) 시 호출 */
export const resetNamingPurchaseHistory = () => {
  allLogs.value = null;
  fetchPromise = null;
  isFetching.value = false;
  fetchEpoch += 1;
};

/**
 * 구매내역 카드 → 결과 페이지에 전달할 `namingSelections` 형태로 변환.
 * modeKey('recommend-person-kor')를 type/target/detail 토큰으로 분해하고,
 * detailInput/사주 정보를 명시적으로 복원한다.
 *
 * 결과 데이터 API가 modeKey/detailInput/guestUserId를 받아 호출되도록 설계되어
 * 있으므로, 이 3개 값이 결과 화면에 그대로 전달되도록 한다.
 */
export const buildNamingSelectionsFromHistoryItem = (item) => {
  if (!item?.modeKey) return null;
  const tokens = item.modeKey.split('-');
  if (tokens.length < 3) return null;
  return {
    type: tokens[0],
    target: tokens[1],
    detail: tokens[2],
    detailInput: { ...(item.detailInput || {}) },
    saju: {
      guestUserId: item.guestUserId,
      guestUserName: item.sajuName || '',
      name: item.sajuName || '',
    },
    analysisLogId: item.analysisLogId,
  };
};

/** 전체 작명 이력을 1회 호출로 조회 후 캐시. 이미 로드되어 있으면 캐시 반환. */
const fetchAll = async () => {
  if (allLogs.value !== null) return allLogs.value;
  if (fetchPromise) return fetchPromise;

  const epoch = fetchEpoch;
  isFetching.value = true;
  fetchPromise = (async () => {
    try {
      const res = await getAnalysisLogAPI({
        analysisTypeId: NAMING_TYPE_ID,
        isValid: true,
        page: 1,
        size: -1,
      });
      const items = (res?.content || []).map((log) => mapLogToHistoryItem(log));
      if (epoch === fetchEpoch) allLogs.value = items;
      return items;
    } catch (e) {
      DEV_MODE && console.error('[낭만작명 구매내역] fetch 실패:', e);
      // 실패를 캐시하지 않는다(allLogs는 null 유지) — 빈 배열로 캐시하면 "구매한 이력이
      // 없습니다"가 다음 resetCache까지 고착됨 (비로그인 401 후 로그인, 일시 네트워크 오류 등).
      // null이면 다음 fetchAll(시트 재오픈·결제 등)에서 자동 재시도된다.
      return [];
    } finally {
      // 리셋(epoch 변경) 이후에는 reset이 상태를 이미 정리했고,
      // 새 fetch가 시작됐을 수 있으므로 stale fetch는 모듈 상태를 건드리지 않는다
      if (epoch === fetchEpoch) {
        isFetching.value = false;
        fetchPromise = null;
      }
    }
  })();
  return fetchPromise;
};

export const useNamingPurchaseHistory = (guestUserId = null) => {
  const list = computed(() => {
    const id = toValue(guestUserId);
    return (allLogs.value || []).filter((item) => !id || Number(item.guestUserId) === Number(id));
  });

  /** 카테고리별 데이터 존재 여부 — 빈 탭 disabled 처리용 */
  const categoryPurchaseMap = computed(() => {
    const map = {};
    for (const tab of PURCHASE_CATEGORY_TABS) map[tab.key] = false;
    for (const item of list.value) {
      const cat = getCategoryOf(item);
      if (cat in map) map[cat] = true;
    }
    return map;
  });

  const filterByCategory = (key) =>
    list.value.filter((item) => getCategoryOf(item) === key);

  return {
    list,
    categoryPurchaseMap,
    filterByCategory,
    fetchAll,
    resetCache: resetNamingPurchaseHistory,
    isFetching,
    PURCHASE_CATEGORY_TABS,
  };
};
